import { postData } from '@/services/axios-service';
import { BASE_URL } from '@/utils/constants/api-constants';
import { i18n } from '@/plugins/i18n';
import { defineStore, storeToRefs } from 'pinia';
import { ISignUpStore } from '@/interfaces/sign-up/signup-store.interface';
import { ISignUpResponse } from '@/interfaces/sign-up/signup-response.interface';
import axios from 'axios';
import { useAuthStore } from './auth-store';
import { useProfileStore } from './profile-store';

const INITIAL_STATE: ISignUpStore = {
  signUpForm: {
    email: undefined,
    password: '',
    acceptTerms: false,
  },
};

export const useSignUpStore = defineStore({
  id: 'signUp',
  state: (): ISignUpStore => ({
    ...INITIAL_STATE,
  }),
  getters: {},
  actions: {
    async signUp() {
      try {
        const { msg, error, status } = await postData<ISignUpResponse>(
          `${BASE_URL.USERS}register`,
          {
            ...this.signUpForm,
          },
        );

        if (msg && status == axios.HttpStatusCode.Ok) {
          return msg;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async verifyToken(token: string) {
      try {
        const { msg, error, status, data } = await postData<ISignUpResponse>(
          `${BASE_URL.USERS}register/verify`,
          {
            token: token,
          },
        );

        if (msg && data && status == axios.HttpStatusCode.Ok) {
          const { token, user } = storeToRefs(useAuthStore());
          const { languageSelected } = storeToRefs(useProfileStore());
          token.value = `Bearer ${data.token}`;
          user.value = data.user;
          languageSelected.value = data.user.Settings?.language ?? 'en';
          return msg;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async verifyPasswordToken(token: string) {
      try {
        const { msg, error, status } = await postData<ISignUpResponse>(
          `${BASE_URL.USERS}recovery/reset-password`,
          {
            token: token,
          },
        );

        if (msg && status == axios.HttpStatusCode.Ok) {
          return msg;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    clearLoginForm() {
      this.signUpForm = {
        email: undefined,
        password: undefined,
        acceptTerms: false,
      };
    },
  },
  persist: {},
});

export type SignUpStore = ReturnType<typeof useSignUpStore>;
